/**
 * 1112 ASSAY charts. Rucni SVG.
 * Referenca: LBMA Good Delivery bar, BullionVault bar-list, Open MCT conductor,
 * MetalDesk workstation. Hatch umjesto fillova: dijagonala / crta / tockice.
 */
import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';

export type TxnType = 'prodaja' | 'otkup_inv' | 'otkup_lom';

export const TYPE_COLOR: Record<TxnType, string> = {
    prodaja: '#f0c14b',
    otkup_inv: '#3db8a8',
    otkup_lom: '#c45a2a',
};

export const TYPE_FACE: Record<TxnType, { top: string; front: string; side: string }> = {
    prodaja: { top: '#f6d56a', front: '#c9a227', side: '#7a5c18' },
    otkup_inv: { top: '#7ee0d4', front: '#2a8f84', side: '#165e56' },
    otkup_lom: { top: '#e08a62', front: '#a44a28', side: '#5c2414' },
};

const nf0 = new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat('hr-HR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const nf2 = new Intl.NumberFormat('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nf3 = new Intl.NumberFormat('hr-HR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

export const TROY_G = 31.1034768;

export function axisNum(v: number): string {
    const a = Math.abs(v);
    if (a >= 1_000_000) return nf2.format(v / 1_000_000) + ' M';
    if (a >= 10_000) return nf0.format(v / 1000) + ' k';
    return nf0.format(v);
}

export function fmtKg(g: number): string {
    if (Math.abs(g) >= 1000) return nf3.format(g / 1000) + ' kg';
    return nf1.format(g) + ' g';
}

export function fmtOz(g: number): string {
    return nf2.format(g / TROY_G) + ' oz';
}

export function hatchUrl(ty: TxnType): string {
    return `url(#a12-h-${ty})`;
}

export function HatchSprite() {
    return (
        <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden>
            <defs>
                <pattern id="a12-h-prodaja" patternUnits="userSpaceOnUse" width="8" height="8">
                    <rect width="8" height="8" fill="#f0c14b" fillOpacity="0.14" />
                    <path d="M-1,9 L9,-1" stroke="#f0c14b" strokeWidth="1.2" />
                </pattern>
                <pattern id="a12-h-otkup_inv" patternUnits="userSpaceOnUse" width="7" height="7">
                    <rect width="7" height="7" fill="#3db8a8" fillOpacity="0.12" />
                    <path d="M0,3.5 H7" stroke="#3db8a8" strokeWidth="1.25" />
                </pattern>
                <pattern id="a12-h-otkup_lom" patternUnits="userSpaceOnUse" width="8" height="8">
                    <rect width="8" height="8" fill="#c45a2a" fillOpacity="0.12" />
                    <circle cx="2.2" cy="2.2" r="1.1" fill="#c45a2a" />
                    <circle cx="6.2" cy="5.8" r="1.1" fill="#c45a2a" />
                </pattern>
                <linearGradient id="a12-gold-sheen" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#fff6c8" stopOpacity="0.55" />
                    <stop offset="42%" stopColor="#f0c14b" stopOpacity="0.12" />
                    <stop offset="100%" stopColor="#6a4e12" stopOpacity="0.35" />
                </linearGradient>
                <filter id="a12-bar-shadow" x="-20%" y="-20%" width="140%" height="160%">
                    <feDropShadow dx="2" dy="4" stdDeviation="3" floodColor="#000" floodOpacity="0.55" />
                </filter>
            </defs>
        </svg>
    );
}

export function TypeSwatch({ type, size = 12 }: { type: TxnType; size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 12 12" aria-hidden>
            <rect width="12" height="12" fill={hatchUrl(type)} stroke={TYPE_COLOR[type]} strokeWidth="1" />
        </svg>
    );
}

/** Isometric Good Delivery bar. Masse first, money second. */
export function GoldIngot({
    type, kgLabel, ozLabel, eurLabel, count, spread, rucSpot, rucFix, active, onClick,
}: {
    type: TxnType;
    kgLabel: string;
    ozLabel: string;
    eurLabel: string;
    count: string;
    spread: string;
    rucSpot: string;
    rucFix: string;
    active: boolean;
    onClick: () => void;
}) {
    const face = TYPE_FACE[type];
    const c = TYPE_COLOR[type];
    return (
        <button type="button" className={'a12-ingot' + (active ? ' is-on' : '')} onClick={onClick} style={{ ['--a12-ty' as string]: c } as CSSProperties}>
            <svg viewBox="0 0 168 78" width="168" height="78" aria-hidden className="a12-ingot-iso">
                <defs>
                    <filter id={`a12-bar-sh-${type}`} x="-20%" y="-20%" width="140%" height="160%">
                        <feDropShadow dx="2" dy="4" stdDeviation="3" floodColor="#000" floodOpacity="0.55" />
                    </filter>
                </defs>
                <g filter={`url(#a12-bar-sh-${type})`} opacity={active ? 1 : 0.42}>
                    <polygon points="28,28 148,28 160,12 40,12" fill={face.top} />
                    <polygon points="148,28 160,12 160,52 148,68" fill={face.side} />
                    <polygon points="28,28 148,28 148,68 28,68" fill={face.front} />
                    <polygon points="28,28 148,28 148,68 28,68" fill={hatchUrl(type)} fillOpacity="0.55" />
                    <polygon points="28,28 148,28 160,12 40,12" fill="url(#a12-gold-sheen)" />
                    <text x="88" y="44" textAnchor="middle" fill="#0a0704" fontSize="9" fontFamily="Liberation Serif, Palatino, serif" letterSpacing="2.4" fontWeight="700">AU 999.9</text>
                    <text x="88" y="58" textAnchor="middle" fill="#0a0704" fontSize="11" fontFamily="Liberation Mono, ui-monospace, monospace">{kgLabel}</text>
                </g>
            </svg>
            <div className="a12-ingot-meta">
                <div className="a12-k">{count}</div>
                <div className="a12-eur">{eurLabel}</div>
                <div className="a12-oz">{ozLabel}</div>
                <div className="a12-dual">
                    <span>SPOT {spread}</span>
                    <span>RUC {rucSpot}</span>
                    <span>FIX {rucFix}</span>
                </div>
            </div>
        </button>
    );
}

type AreaProps = {
    labels: string[];
    series: Record<string, number[]>;
    types: TxnType[];
    height?: number;
    onPick?: (index: number, label: string, values: Record<string, number>) => void;
    onHover?: (index: number | null, label: string, values: Record<string, number>, total: number) => void;
    picked?: number | null;
    formatLabel?: (iso: string) => string;
};

export function HatchArea({ labels, series, types, height = 268, onPick, picked, formatLabel, onHover }: AreaProps) {
    const W = 920;
    const H = height;
    const pad = { l: 52, r: 16, t: 14, b: 30 };
    const innerW = W - pad.l - pad.r;
    const innerH = H - pad.t - pad.b;
    const n = Math.max(1, labels.length);
    const [hover, setHover] = useState<number | null>(null);

    const stacks = useMemo(() => {
        const tot: number[] = labels.map((_, i) => types.reduce((s, ty) => s + (series[ty]?.[i] ?? 0), 0));
        const max = Math.max(1, ...tot);
        const bands: { ty: TxnType; d: string }[] = [];
        const acc = labels.map(() => 0);
        for (const ty of types) {
            const pts: { x: number; y0: number; y1: number }[] = [];
            for (let i = 0; i < n; i++) {
                const v = series[ty]?.[i] ?? 0;
                const y0 = acc[i];
                acc[i] += v;
                pts.push({
                    x: n === 1 ? innerW / 2 : (i / (n - 1)) * innerW,
                    y0,
                    y1: acc[i],
                });
            }
            const top = pts.map((p) => `${p.x.toFixed(1)},${((1 - p.y1 / max) * innerH).toFixed(1)}`).join(' ');
            const bot = [...pts].reverse().map((p) => `${p.x.toFixed(1)},${((1 - p.y0 / max) * innerH).toFixed(1)}`).join(' ');
            bands.push({ ty, d: `M ${top} L ${bot} Z` });
        }
        const totalLine = tot.map((v, i) => {
            const x = n === 1 ? innerW / 2 : (i / (n - 1)) * innerW;
            const y = (1 - v / max) * innerH;
            return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
        }).join(' ');
        return { bands, max, tot, totalLine };
    }, [labels, series, types, n, innerW, innerH]);

    const idxAt = (ev: { currentTarget: SVGSVGElement; clientX: number }) => {
        const svg = ev.currentTarget;
        const r = svg.getBoundingClientRect();
        const x = ((ev.clientX - r.left) / r.width) * W - pad.l;
        if (n <= 1) return 0;
        return Math.max(0, Math.min(n - 1, Math.round((x / innerW) * (n - 1))));
    };

    const active = hover ?? picked ?? null;
    const ax = active == null ? null : (n === 1 ? innerW / 2 : (active / (n - 1)) * innerW);
    const valuesAt = (i: number) => {
        const values: Record<string, number> = {};
        for (const ty of types) values[ty] = series[ty]?.[i] ?? 0;
        return values;
    };

    return (
        <svg
            viewBox={`0 0 ${W} ${H}`}
            width="100%"
            height={H}
            className="a12-chart"
            onPointerMove={(e) => {
                const i = idxAt(e);
                setHover(i);
                onHover?.(i, labels[i] ?? '', valuesAt(i), stacks.tot[i] ?? 0);
            }}
            onPointerLeave={() => {
                setHover(null);
                onHover?.(null, '', {}, 0);
            }}
            onClick={(e) => {
                const i = idxAt(e);
                onPick?.(i, labels[i] ?? '', valuesAt(i));
            }}
        >
            <g transform={`translate(${pad.l},${pad.t})`}>
                {[0, 0.25, 0.5, 0.75, 1].map((t) => {
                    const y = (1 - t) * innerH;
                    return (
                        <g key={t}>
                            <line x1={0} x2={innerW} y1={y} y2={y} stroke="#2a2218" strokeWidth={1} />
                            <text x={-8} y={y + 3} textAnchor="end" fill="#8a7a66" fontSize={10} fontFamily="Liberation Mono, ui-monospace, monospace">
                                {axisNum(stacks.max * t)}
                            </text>
                        </g>
                    );
                })}
                {stacks.bands.map((b) => (
                    <path key={b.ty} d={b.d} fill={hatchUrl(b.ty)} stroke={TYPE_COLOR[b.ty]} strokeWidth={1.05} />
                ))}
                <path d={stacks.totalLine} fill="none" stroke="#f0c14b" strokeWidth={1.6} />
                {ax != null && active != null ? (
                    <line x1={ax} x2={ax} y1={0} y2={innerH} stroke="#f0c14b" strokeWidth={1} strokeDasharray="3 3" />
                ) : null}
                {labels.map((lb, i) => {
                    const x = n === 1 ? innerW / 2 : (i / (n - 1)) * innerW;
                    const show = i === 0 || i === n - 1 || i % Math.ceil(n / 8) === 0;
                    return show ? (
                        <text key={lb + i} x={x} y={innerH + 16} textAnchor="middle" fill="#8a7a66" fontSize={9} fontFamily="Liberation Mono, ui-monospace, monospace">
                            {formatLabel ? formatLabel(lb) : lb.slice(5)}
                        </text>
                    ) : null;
                })}
            </g>
        </svg>
    );
}

type RaceRow = { key: string; sub?: string; value: number; parts: { ty: TxnType; v: number }[]; g?: number; broj?: number };

export function RaceBars({ rows, maxRows = 14, onPick, picked }: {
    rows: RaceRow[]; maxRows?: number; onPick?: (key: string) => void; picked?: string | null;
}) {
    const shown = rows.slice(0, maxRows);
    const max = Math.max(1, ...shown.map((r) => r.value));
    return (
        <div className="a12-race">
            {shown.map((r, i) => (
                <button
                    key={r.key}
                    type="button"
                    className={'a12-race-row' + (picked === r.key ? ' is-on' : '')}
                    onClick={() => onPick?.(r.key)}
                >
                    <span className="a12-race-n">{String(i + 1).padStart(2, '0')}</span>
                    <div className="a12-race-body">
                        <div className="a12-race-name">
                            {r.key}
                            {r.sub ? <em>{r.sub}</em> : null}
                        </div>
                        <div className="a12-race-track">
                            <svg width="100%" height="12" preserveAspectRatio="none" viewBox="0 0 100 12">
                                {(() => {
                                    let x = 0;
                                    return r.parts.map((p) => {
                                        const w = (p.v / max) * 100;
                                        const el = (
                                            <rect key={p.ty} x={x} y={1} width={Math.max(0, w)} height={10} fill={hatchUrl(p.ty)} stroke={TYPE_COLOR[p.ty]} strokeWidth={0.4} />
                                        );
                                        x += w;
                                        return el;
                                    });
                                })()}
                            </svg>
                        </div>
                    </div>
                    <div className="a12-race-val">
                        <strong>{axisNum(r.value)}</strong>
                        {r.g != null ? <span>{fmtKg(r.g)}</span> : null}
                    </div>
                </button>
            ))}
        </div>
    );
}

type HeatCell = { dow: number; hour: number; type: TxnType; broj: number; eur: number };

export function HeatGrid({ cells, types, metric, onPick }: {
    cells: HeatCell[]; types: TxnType[]; metric: 'eur' | 'broj';
    onPick?: (dow: number, hour: number, value: number) => void;
}) {
    const grid = useMemo(() => {
        const m = new Map<string, number>();
        let max = 1;
        for (const c of cells) {
            if (!types.includes(c.type)) continue;
            const k = `${c.dow}:${c.hour}`;
            const add = metric === 'eur' ? c.eur : c.broj;
            const next = (m.get(k) ?? 0) + add;
            m.set(k, next);
            if (next > max) max = next;
        }
        return { m, max };
    }, [cells, types, metric]);

    const days = ['pon', 'uto', 'sri', 'cet', 'pet', 'sub', 'ned'];
    const cell = 22;
    const hours = Array.from({ length: 24 }, (_, i) => i);

    return (
        <svg viewBox={`0 0 ${56 + 24 * cell} ${28 + 7 * cell}`} width="100%" className="a12-chart">
            {hours.filter((h) => h % 2 === 0).map((h) => (
                <text key={h} x={56 + h * cell + cell / 2} y={12} textAnchor="middle" fill="#8a7a66" fontSize={9} fontFamily="Liberation Mono, ui-monospace, monospace">{String(h).padStart(2, '0')}</text>
            ))}
            {days.map((d, dow) => (
                <g key={d} transform={`translate(0, ${20 + dow * cell})`}>
                    <text x={48} y={14} textAnchor="end" fill="#8a7a66" fontSize={10} fontFamily="Liberation Mono, ui-monospace, monospace">{d}</text>
                    {hours.map((h) => {
                        const v = grid.m.get(`${dow}:${h}`) ?? 0;
                        const t = v / grid.max;
                        return (
                            <rect
                                key={h}
                                x={56 + h * cell}
                                y={2}
                                width={cell - 3}
                                height={cell - 3}
                                fill={`rgba(240,193,75,${(0.05 + t * 0.92).toFixed(3)})`}
                                stroke="#2a2218"
                                strokeWidth={0.6}
                                style={{ cursor: 'pointer' }}
                                onClick={() => onPick?.(dow, h, v)}
                            />
                        );
                    })}
                </g>
            ))}
        </svg>
    );
}

export function MixPlate({ parts, onPick, picked }: {
    parts: { key: TxnType; eur: number; fine_g: number; broj: number }[];
    onPick?: (key: TxnType) => void;
    picked?: TxnType | null;
}) {
    const total = parts.reduce((s, p) => s + p.eur, 0) || 1;
    const totalG = parts.reduce((s, p) => s + p.fine_g, 0);
    let a = 0;
    const slices = parts.map((p) => {
        const frac = p.eur / total;
        const start = a;
        a += frac;
        return { ...p, start, frac };
    });
    const R = 62;
    const CX = 78;
    const CY = 78;
    const arc = (start: number, frac: number) => {
        if (frac >= 0.999) return `M ${CX} ${CY - R} A ${R} ${R} 0 1 1 ${CX - 0.01} ${CY - R} Z`;
        const s = start * Math.PI * 2 - Math.PI / 2;
        const e = (start + frac) * Math.PI * 2 - Math.PI / 2;
        const large = frac > 0.5 ? 1 : 0;
        return `M ${CX} ${CY} L ${CX + Math.cos(s) * R} ${CY + Math.sin(s) * R} A ${R} ${R} 0 ${large} 1 ${CX + Math.cos(e) * R} ${CY + Math.sin(e) * R} Z`;
    };

    return (
        <div className="a12-mix">
            <svg width={156} height={156}>
                <circle cx={CX} cy={CY} r={R + 6} fill="none" stroke="#3a2e22" strokeWidth="1" />
                {slices.map((s) => (
                    <path
                        key={s.key}
                        d={arc(s.start, s.frac)}
                        fill={hatchUrl(s.key)}
                        stroke={picked === s.key ? '#f4ead8' : TYPE_COLOR[s.key]}
                        strokeWidth={picked === s.key ? 2.2 : 1.1}
                        style={{ cursor: 'pointer' }}
                        onClick={() => onPick?.(s.key)}
                    />
                ))}
                <circle cx={CX} cy={CY} r={28} fill="#0e0a07" stroke="#3a2e22" />
                <text x={CX} y={CY - 2} textAnchor="middle" fill="#f0c14b" fontSize={11} fontFamily="Liberation Mono, ui-monospace, monospace">{fmtKg(totalG)}</text>
                <text x={CX} y={CY + 12} textAnchor="middle" fill="#8a7a66" fontSize={9} fontFamily="Liberation Mono, ui-monospace, monospace">999.9</text>
            </svg>
            <div className="a12-mix-legend">
                {parts.map((p) => (
                    <button key={p.key} type="button" className={'a12-mix-row' + (picked === p.key ? ' is-on' : '')} onClick={() => onPick?.(p.key)}>
                        <TypeSwatch type={p.key} />
                        <span className="a12-mix-k">{p.key}</span>
                        <span className="a12-mix-v">{axisNum(p.eur)}</span>
                        <span className="a12-mix-g">{fmtKg(p.fine_g)}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}

export function CompareLanes({ labels, a, b, aLabel, bLabel, onPick }: {
    labels: string[];
    a: number[];
    b: number[];
    aLabel: string;
    bLabel: string;
    onPick?: (index: number, label: string) => void;
}): ReactNode {
    const max = Math.max(1, ...a, ...b);
    const W = 920;
    const H = 240;
    const pad = { l: 52, r: 16, t: 22, b: 28 };
    const innerW = W - pad.l - pad.r;
    const innerH = H - pad.t - pad.b;
    const n = Math.max(1, labels.length);
    const bw = innerW / n;
    return (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} className="a12-chart">
            {[0, 0.5, 1].map((t) => {
                const y = pad.t + (1 - t) * innerH;
                return (
                    <g key={t}>
                        <line x1={pad.l} x2={W - pad.r} y1={y} y2={y} stroke="#2a2218" />
                        <text x={pad.l - 8} y={y + 3} textAnchor="end" fill="#8a7a66" fontSize={10} fontFamily="Liberation Mono, ui-monospace, monospace">{axisNum(max * t)}</text>
                    </g>
                );
            })}
            {labels.map((lb, i) => {
                const x = pad.l + i * bw;
                const ha = (a[i] ?? 0) / max * innerH;
                const hb = (b[i] ?? 0) / max * innerH;
                return (
                    <g key={lb + i} onClick={() => onPick?.(i, lb)} style={{ cursor: onPick ? 'pointer' : 'default' }}>
                        <rect x={x + bw * 0.16} y={pad.t + innerH - ha} width={bw * 0.30} height={ha} fill={hatchUrl('prodaja')} stroke="#f0c14b" strokeWidth={0.8} />
                        <rect x={x + bw * 0.52} y={pad.t + innerH - hb} width={bw * 0.30} height={hb} fill="none" stroke="#8a7a66" strokeWidth={1.15} strokeDasharray="3 2" />
                        {(i === 0 || i === n - 1 || i % Math.ceil(n / 8) === 0) ? (
                            <text x={x + bw / 2} y={H - 8} textAnchor="middle" fill="#8a7a66" fontSize={9} fontFamily="Liberation Mono, ui-monospace, monospace">{lb.slice(5)}</text>
                        ) : null}
                    </g>
                );
            })}
            <text x={pad.l} y={14} fill="#f0c14b" fontSize={10} fontFamily="Liberation Sans Narrow, sans-serif" letterSpacing="1.4">{aLabel}</text>
            <text x={pad.l + 140} y={14} fill="#8a7a66" fontSize={10} fontFamily="Liberation Sans Narrow, sans-serif" letterSpacing="1.4">{bLabel} (crtano)</text>
        </svg>
    );
}

/** Net metal tank: sold vs bought fine grams. Positive = net outflow. */
export function NetTank({ soldG, boughtG }: { soldG: number; boughtG: number }) {
    const tot = Math.max(1, soldG + boughtG);
    const soldPct = soldG / tot;
    const net = soldG - boughtG;
    return (
        <div className="a12-tank">
            <div className="a12-tank-lab">NET METAL</div>
            <div className="a12-tank-track">
                <div className="a12-tank-sold" style={{ width: `${soldPct * 100}%` }} />
                <div className="a12-tank-bought" style={{ width: `${(1 - soldPct) * 100}%` }} />
            </div>
            <div className="a12-tank-nums">
                <span>OUT {fmtKg(soldG)}</span>
                <strong className={net >= 0 ? 'pos' : 'neg'}>{net >= 0 ? '+' : ''}{fmtKg(net)}</strong>
                <span>IN {fmtKg(boughtG)}</span>
            </div>
            <div className="a12-tank-oz">{fmtOz(Math.abs(net))} troy</div>
        </div>
    );
}

/** Three needles: realized vs spot vs fixing, on one €/g scale. */
export function TripleQuote({ realized, spot, fixing }: { realized: number | null; spot: number | null; fixing: number | null }) {
    const vals = [realized, spot, fixing].filter((v): v is number => v != null && v > 0);
    const min = vals.length ? Math.min(...vals) * 0.985 : 0;
    const max = vals.length ? Math.max(...vals) * 1.015 : 1;
    const span = Math.max(0.01, max - min);
    const pos = (v: number | null) => (v == null ? null : ((v - min) / span) * 100);
    const pR = pos(realized);
    const pS = pos(spot);
    const pF = pos(fixing);
    return (
        <div className="a12-triple">
            <div className="a12-triple-lab">€ / g · REAL / SPOT / FIX</div>
            <div className="a12-triple-rail">
                {pS != null ? <i className="n-spot" style={{ left: `${pS}%` }} title={`spot ${nf2.format(spot ?? 0)}`} /> : null}
                {pF != null ? <i className="n-fix" style={{ left: `${pF}%` }} title={`fixing ${nf2.format(fixing ?? 0)}`} /> : null}
                {pR != null ? <i className="n-real" style={{ left: `${pR}%` }} title={`realized ${nf2.format(realized ?? 0)}`} /> : null}
            </div>
            <div className="a12-triple-nums">
                <span className="real">{realized != null ? nf2.format(realized) : '—'}</span>
                <span className="spot">{spot != null ? nf2.format(spot) : '—'}</span>
                <span className="fix">{fixing != null ? nf2.format(fixing) : '—'}</span>
            </div>
        </div>
    );
}

export function Readout({ children, onClose }: { children: ReactNode; onClose?: () => void }) {
    return (
        <div className="a12-readout">
            <button type="button" className="a12-readout-x" onClick={onClose}>x</button>
            {children}
        </div>
    );
}
